/**
 * Keys Page
 *
 * API key management page with create, view, and revoke functionality.
 *
 * @module pages/KeysPage
 */

import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle, useApiKeys, usePermissions } from '@/hooks';
import { Card, Button, Spinner, Alert, Tooltip } from '@/components/ui';
import {
  CreateKeyModal,
  KeyCreatedModal,
  RevokeKeyModal,
  KeyCard,
  UpgradeToCreateKeysModal,
} from '@/components/keys';
import { Plus, Key } from 'lucide-react';
import { DEFAULT_PLANS, type ApiKey } from '@/lib/firebase-types';

// =============================================================================
// Component
// =============================================================================

export function KeysPage() {
  usePageTitle('API Keys');

  const { organization } = useAuth();
  const { canManageApiKeys } = usePermissions();
  const orgId = organization?.id;
  const planId = organization?.planId || 'free';
  const isInternalAccount = organization?.accountType === 'internal';

  // Internal accounts have unlimited keys (-1)
  const maxKeys = isInternalAccount
    ? -1
    : (DEFAULT_PLANS[planId]?.features.maxApiKeys ?? 1);

  const {
    keys,
    loading,
    error,
    keyCount,
    createKey,
    revokeKey,
  } = useApiKeys(orgId);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [defaultLabel, setDefaultLabel] = useState('');

  const [createdKey, setCreatedKey] = useState<{
    key: string;
    label: string;
  } | null>(null);

  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  // Check if at limit (only applies to non-internal accounts)
  const atLimit = !isInternalAccount && maxKeys !== -1 && keyCount >= maxKeys;

  const handleOpenCreateModal = (label = '') => {
    // If at limit on free plan, show upgrade modal instead
    if (atLimit && planId === 'free') {
      setUpgradeModalOpen(true);
      return;
    }

    setDefaultLabel(label);
    setCreateModalOpen(true);
  };

  const handleCreateKey = async (label: string) => {
    const fullKey = await createKey(label);
    setCreateModalOpen(false);
    setCreatedKey({ key: fullKey, label });
  };

  const handleRevokeKey = async () => {
    if (!revokeTarget) return;
    await revokeKey(revokeTarget.id);
    setRevokeTarget(null);
  };

  // ---------------------------------------------------------------------------
  // Filter keys
  // ---------------------------------------------------------------------------

  const activeKeys = keys.filter((k) => k.status === 'active');
  const revokedKeys = keys.filter((k) => k.status === 'revoked');

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-semibold text-gray-900">
            API Keys
          </h1>
        </div>
        <Tooltip
          content={!canManageApiKeys ? "You don't have permission to create API keys" : ''}
          position="left"
          width="auto"
        >
          <Button
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => handleOpenCreateModal()}
            disabled={!canManageApiKeys}
          >
            Create Key
          </Button>
        </Tooltip>
      </div>

      {/* Plan limit indicator */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <span>API keys:</span>
        <span className="font-medium text-gray-900">
          {keyCount} / {maxKeys === -1 ? '∞' : maxKeys}
        </span>
        {atLimit && (
          <span className="text-warning-600">(Limit reached)</span>
        )}
      </div>

      {/* Error state */}
      {error && (
        <Alert variant="error">{error}</Alert>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {/* Empty state */}
      {!loading && keys.length === 0 && (
        <Card className="text-center py-12">
          <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <Key className="h-6 w-6 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            No API keys yet
          </h3>
          <p className="text-gray-500 mb-4">
            {canManageApiKeys
              ? 'Create your first API key to start using the Hummingbird API.'
              : 'No API keys have been created yet. Contact an admin to create one.'}
          </p>
          {canManageApiKeys && (
            <Button onClick={() => handleOpenCreateModal()}>
              Create Your First Key
            </Button>
          )}
        </Card>
      )}

      {/* Active Keys List */}
      {!loading && activeKeys.length > 0 && (
        <Card noPadding>
          <div className="divide-y divide-gray-200">
            {activeKeys.map((key) => (
              <KeyCard
                key={key.id}
                apiKey={key}
                onRevoke={setRevokeTarget}
                canRevoke={canManageApiKeys}
              />
            ))}
          </div>
        </Card>
      )}

      {/* Revoked Keys (collapsed) */}
      {!loading && revokedKeys.length > 0 && (
        <details className="group">
          <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
            Show {revokedKeys.length} revoked key{revokedKeys.length !== 1 ? 's' : ''}
          </summary>
          <Card noPadding className="mt-2">
            <div className="divide-y divide-gray-200">
              {revokedKeys.map((key) => (
                <KeyCard
                  key={key.id}
                  apiKey={key}
                  onRevoke={() => {}}
                  canRevoke={false}
                />
              ))}
            </div>
          </Card>
        </details>
      )}

      {/* Security Info */}
      <Card className="bg-gray-50 border-dashed">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-white rounded-lg border border-gray-200">
            <Key className="h-5 w-5 text-gray-400" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">API Key Security</h3>
            <p className="text-sm text-gray-500 mt-1">
              Your API keys carry important privileges. Keep them secure and never share them
              in public repositories or client-side code. Use environment variables instead.
            </p>
          </div>
        </div>
      </Card>

      {/* Create Key Modal */}
      <CreateKeyModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateKey}
        defaultLabel={defaultLabel}
        keyCount={keyCount}
        maxKeys={maxKeys}
      />

      {/* Key Created Modal */}
      {createdKey && (
        <KeyCreatedModal
          open={true}
          onClose={() => setCreatedKey(null)}
          apiKey={createdKey.key}
          label={createdKey.label}
        />
      )}

      {/* Revoke Key Modal */}
      {revokeTarget && (
        <RevokeKeyModal
          open={true}
          onClose={() => setRevokeTarget(null)}
          onConfirm={handleRevokeKey}
          keyLabel={revokeTarget.label}
          keyDisplay={`${revokeTarget.keyPrefix}...${revokeTarget.keySuffix}`}
        />
      )}

      {/* Upgrade Modal (for free users at limit) */}
      <UpgradeToCreateKeysModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
      />
    </div>
  );
}
